import { ID } from '@griply/types';
import {
  TreeItem,
  TreeItemTypes,
} from 'shared/components/ui/virtualized-tree-list';

export type ReorderSectionIds = {
  newSectionId?: ID;
  oldSectionId?: ID;
};

/**
 * Determine the new and old section ids for a task that just moved, using
 * only the `updatedItems` returned by the tree library's drop handler —
 * NOT the current tree data.
 *
 * Looking up the old section in the full tree data is unsafe on Upcoming
 * because habits/repeating tasks appear in multiple day sections, and
 * `Object.values(tree).find(...)` returns the first match instead of the
 * section the user actually dragged from.
 *
 * The tree library already encodes the correct information in `updatedItems`:
 * the section the task was removed from has the task missing from its
 * children, and the section the task was added to has it present. For a
 * same-section move there is only one affected section and it still
 * contains the task — in which case old equals new.
 */
export const getReorderSectionIds = (
  itemId: ID,
  updatedItems: TreeItem[],
): ReorderSectionIds => {
  const affectedSections = updatedItems.filter(
    (item) => item.type === TreeItemTypes.Date,
  );

  const newSectionItem = affectedSections.find((section) =>
    section.children?.includes(itemId),
  );

  if (!newSectionItem) {
    return { newSectionId: undefined, oldSectionId: undefined };
  }

  const oldSectionItem =
    affectedSections.find(
      (section) =>
        section.id !== newSectionItem.id && !section.children?.includes(itemId),
    ) ?? newSectionItem;

  return {
    newSectionId: newSectionItem.id,
    oldSectionId: oldSectionItem.id,
  };
};
